import { Box, Flex } from "@rebass/grid";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { useLatePaymentPenaltyConfig } from "@rentiohq/shared-frontend/dist/reduxV2/preferences/preference.hooks";
import {
  EPenaltyAmountType,
  ILatePaymentPenaltyConfig,
} from "@rentiohq/shared-frontend/dist/reduxV2/preferences/preference.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Banner,
  Button,
  Card,
  DisplayText,
  Grid,
  Icon,
  Label,
  Loading,
  SegmentedControl,
  Switch,
  TextField,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import { TAssetSource } from "@rentiohq/web-shared/dist/components/Icon";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { ESettingsPaths } from "../../Settings.types";

const AFTER_CREATED_FACTS: { icon: TAssetSource; key: string }[] = [
  { icon: "calendar", key: "due" },
  { icon: "billStack", key: "payment_methods" },
  { icon: "email", key: "weekly_reminder" },
  { icon: "checkDouble", key: "one_per_charge" },
];

/**
 * Shared by both columns so the penalty and the administrative fee get identical header
 * rows. The administrative fee renders the same two options but disabled, which says
 * "percentage is not on the table here" without looking like a different kind of control.
 *
 * Built per render rather than at module scope so the labels follow a language switch.
 */
const getAmountTypeOptions = () => [
  {
    id: EPenaltyAmountType.Percentage,
    value: getLocalizedText(
      "late_payment_penalty.settings.amount_type.percentage_short",
    ),
  },
  {
    id: EPenaltyAmountType.Fixed,
    value: getLocalizedText(
      "late_payment_penalty.settings.amount_type.fixed_short",
    ),
  },
];

const LatePaymentPenaltyPreferences = () => {
  const {
    data,
    isFetching,
    hasFeature,
    isUpdating,
    fetchError,
    refetch,
    update,
  } = useLatePaymentPenaltyConfig();
  const [config, setConfig] = useState<ILatePaymentPenaltyConfig>();
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setConfig(data);
    setHasChanges(false);
  }, [data]);

  if (isFetching) {
    return <Loading />;
  }

  // The tab is hidden without the office feature; this covers opening the screen by its url.
  if (!hasFeature) {
    return <Navigate to={`/${ESettingsPaths.Base}`} replace={true} />;
  }

  // The fetch is the only way into this screen, so a failure has to offer a way
  // back instead of leaving a spinner up forever.
  if (fetchError || !config) {
    return (
      <Banner
        hasDismiss={false}
        variation="error"
        icon="alertDiamond"
        title={getLocalizedText("general.error")}
      >
        <Spacer weight={ESpacerWeight.W08} />
        <Button appearance="outline" onClick={() => void refetch()}>
          {getLocalizedText("system.reload")}
        </Button>
      </Banner>
    );
  }

  const applyChange = (changes: Partial<ILatePaymentPenaltyConfig>) => {
    setConfig(current => (current ? { ...current, ...changes } : current));
    setHasChanges(true);
  };

  const setNumber = (key: keyof ILatePaymentPenaltyConfig, value: string) => {
    applyChange({ [key]: Number(value) });
  };

  const amountTypeOptions = getAmountTypeOptions();

  const isPercentage =
    config.penaltyAmountType === EPenaltyAmountType.Percentage;

  const handleSave = async () => {
    // Only a saved config clears the unsaved-changes warning; a failed save keeps it up.
    if (await update(config)) {
      setHasChanges(false);
    }
  };

  const renderSectionHeader = (params: {
    title: string;
    description: string;
    children?: React.ReactNode;
  }) => (
    <Flex alignItems="flex-start" justifyContent="space-between">
      <Box mr={3}>
        <DisplayText size="small" space="tight">
          {params.title}
        </DisplayText>
        <TextStyle variation="subdued">{params.description}</TextStyle>
      </Box>
      {params.children}
    </Flex>
  );

  return (
    <Box>
      <Card>
        {renderSectionHeader({
          title: getLocalizedText("late_payment_penalty.settings.title"),
          description: getLocalizedText(
            "late_payment_penalty.settings.description",
          ),
          children: (
            <Flex alignItems="center" flex="0 0 auto">
              <Box mr={2}>
                <Label>
                  {getLocalizedText("late_payment_penalty.settings.enabled")}
                </Label>
              </Box>
              <Switch
                isChecked={config.enabled}
                onChange={enabled => applyChange({ enabled })}
              />
            </Flex>
          ),
        })}

        <Spacer weight={ESpacerWeight.W24} />

        {/* Mirrors the approved mockup: the body is dimmed and inert while the feature is
            off, so it reads as "nothing here applies" instead of an editable form. */}
        <Box
          style={{
            opacity: config.enabled ? 1 : 0.4,
            pointerEvents: config.enabled ? "auto" : "none",
          }}
        >
          <Banner
            hasDismiss={false}
            variation="warning"
            icon="leaseContract"
            title={getLocalizedText("late_payment_penalty.settings.info")}
          />

          <Spacer weight={ESpacerWeight.W08} />

          <Banner
            hasDismiss={false}
            variation="info"
            icon="informationCircle"
            title={getLocalizedText("late_payment_penalty.settings.disclaimer")}
          />

          <Spacer weight={ESpacerWeight.W24} />

          <Grid spacing="base" flexWrap="wrap">
            <Grid.Item width={[1, 1, 1 / 2]}>
              <Flex alignItems="center" justifyContent="space-between">
                <Label>
                  {getLocalizedText(
                    "late_payment_penalty.settings.penalty_amount",
                  )}
                </Label>
                {/* Segments are flex:1 and their labels render in upper case, so a squeezed
                    control wraps. Single-character labels plus a fixed width keep both
                    columns' header rows exactly the same height. */}
                <Box width="96px" flex="0 0 auto">
                  <SegmentedControl
                    name="penaltyAmountType"
                    size="small"
                    selected={config.penaltyAmountType}
                    options={amountTypeOptions}
                    onChange={penaltyAmountType =>
                      applyChange({
                        penaltyAmountType:
                          penaltyAmountType as EPenaltyAmountType,
                      })
                    }
                  />
                </Box>
              </Flex>

              <Spacer weight={ESpacerWeight.W08} />

              {/* label + labelHidden is required, not decorative: TextField only wraps in
                  Labelled when a label is set, and helpText is dropped silently otherwise.
                  The visible label lives in the row above, next to the type control. */}
              {isPercentage ? (
                <TextField
                  name="penaltyPercentage"
                  type="number"
                  min={0}
                  max={100}
                  suffix="%"
                  label={getLocalizedText(
                    "late_payment_penalty.settings.penalty_amount",
                  )}
                  labelHidden
                  value={config.penaltyPercentage}
                  helpText={getLocalizedText(
                    "late_payment_penalty.settings.penalty_amount.help.percentage",
                  )}
                  onChange={({
                    target: { value },
                  }: {
                    target: { value: string };
                  }) => setNumber("penaltyPercentage", value)}
                />
              ) : (
                <TextField
                  name="penaltyFixedAmount"
                  type="number"
                  min={0}
                  prefix="€"
                  label={getLocalizedText(
                    "late_payment_penalty.settings.penalty_amount",
                  )}
                  labelHidden
                  value={config.penaltyFixedAmount}
                  helpText={getLocalizedText(
                    "late_payment_penalty.settings.penalty_amount.help.fixed",
                  )}
                  onChange={({
                    target: { value },
                  }: {
                    target: { value: string };
                  }) => setNumber("penaltyFixedAmount", value)}
                />
              )}
            </Grid.Item>

            <Grid.Item width={[1, 1, 1 / 2]}>
              <Flex alignItems="center" justifyContent="space-between">
                <Label>
                  {getLocalizedText("late_payment_penalty.settings.admin_fee")}
                </Label>
                {/* Static counterpart to the penalty's segmented control, at the same
                    width so both column header rows line up. Reads as "this one has no
                    choice to make": the administrative fee is always a fixed amount. */}
                <Box width="96px" flex="0 0 auto">
                  <SegmentedControl
                    name="adminFeeAmountType"
                    size="small"
                    disabled
                    selected={EPenaltyAmountType.Fixed}
                    options={amountTypeOptions}
                    onChange={() => undefined}
                  />
                </Box>
              </Flex>

              <Spacer weight={ESpacerWeight.W08} />

              <TextField
                name="adminFeeAmount"
                type="number"
                min={0}
                prefix="€"
                label={getLocalizedText(
                  "late_payment_penalty.settings.admin_fee",
                )}
                labelHidden
                value={config.adminFeeAmount}
                helpText={getLocalizedText(
                  "late_payment_penalty.settings.admin_fee.help",
                )}
                onChange={({
                  target: { value },
                }: {
                  target: { value: string };
                }) => setNumber("adminFeeAmount", value)}
              />
            </Grid.Item>

            <Grid.Item width={[1, 1, 1 / 2]}>
              <TextField
                name="daysOverdue"
                type="number"
                min={1}
                suffix={getLocalizedText("late_payment_penalty.settings.days")}
                label={getLocalizedText(
                  "late_payment_penalty.settings.days_overdue",
                )}
                value={config.daysOverdue}
                helpText={getLocalizedText(
                  "late_payment_penalty.settings.days_overdue.help",
                )}
                onChange={({
                  target: { value },
                }: {
                  target: { value: string };
                }) => setNumber("daysOverdue", value)}
              />
            </Grid.Item>
          </Grid>

          <Spacer weight={ESpacerWeight.W24} />

          <Card appearance="subdued" variation="compact" hasBorder={false}>
            <Flex alignItems="flex-start">
              <Box mr={2}>
                <Icon source="informationCircle" size="small" />
              </Box>
              <TextStyle variation="subdued">
                {getLocalizedText("late_payment_penalty.settings.opt_in_note")}
              </TextStyle>
            </Flex>
          </Card>
        </Box>
      </Card>

      <Spacer weight={ESpacerWeight.W16} />

      <Card>
        {renderSectionHeader({
          title: getLocalizedText(
            "late_payment_penalty.settings.after_created.title",
          ),
          description: getLocalizedText(
            "late_payment_penalty.settings.after_created.description",
          ),
        })}

        <Spacer weight={ESpacerWeight.W24} />

        <Grid spacing="base" flexWrap="wrap">
          {AFTER_CREATED_FACTS.map(fact => (
            <Grid.Item key={fact.key} width={[1, 1, 1 / 2]}>
              <Flex alignItems="flex-start">
                <Box mr={2}>
                  <Icon source={fact.icon} size="small" />
                </Box>
                <TextStyle variation="subdued">
                  {getLocalizedText(
                    `late_payment_penalty.settings.after_created.${fact.key}`,
                  )}
                </TextStyle>
              </Flex>
            </Grid.Item>
          ))}
        </Grid>
      </Card>

      {hasChanges && (
        <>
          <Spacer weight={ESpacerWeight.W16} />
          <Banner
            hasDismiss={false}
            variation="warning"
            icon="informationCircle"
            title={getLocalizedText("late_payment_penalty.settings.warning")}
          />
        </>
      )}

      <Spacer weight={ESpacerWeight.W24} />

      <Button
        appearance="primary"
        isSubmitting={isUpdating}
        onClick={() => void handleSave()}
      >
        {getLocalizedText("system.save")}
      </Button>

      <Spacer weight={ESpacerWeight.W08} />

      <Flex alignItems="center">
        <Box mr={2}>
          <Icon source="questionMark" size="small" />
        </Box>
        <TextStyle variation="subdued">
          {getLocalizedText("late_payment_penalty.settings.admin_only_note")}
        </TextStyle>
      </Flex>
    </Box>
  );
};

// eslint-disable-next-line import/no-default-export
export default LatePaymentPenaltyPreferences;
